/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

import {
    Box,
    Button,
    Skeleton,
    Text,
    GridItem,
    Grid,
    HStack,
    Stack,
    Flex,
    useMultiStyleConfig
} from '@chakra-ui/react'
import {CRO_EXPERIMENTS, TILE_CONTEXT_ACCESSORIES} from '../../constants'
import {useDerivedProduct} from '../../hooks'
import {noop} from '../../utils/utils'

// project components
import ImageGallerySkeleton from '../../components/image-gallery/skeleton'
import ImageGallery from '../../components/image-gallery'
import {findImagesBy} from '../../utils/image-groups-utils'
import BadgeGroup from '../../components/badge-group'
import {useToast} from '../../hooks/use-toast'
import Link from '../../components/link'
import {productUrlBuilder} from '../../utils/url'
import ColorVariationSelection from '../../components/color-variation-selection'
import ProductVRLinks from '../../components/product-vr-links'
import AddToCartCTA from '../../components/add-to-cart-cta'
import MaterialVariationSelection from '../../components/material-variation-selection'
import {
    trackProductViewColorSelect,
    trackProductViewConfigurator,
    trackProductViewAddToCart,
    trackProductViewViewProduct
} from '../../tracking/analytics'
import BVProductRating from '../../components/bazaarvoice/rating'

import {hasBVProductRating} from '../../components/bazaarvoice/utils'
import useEffectEvent from '../../hooks/use-effect-event'
import {MAIN_IMAGES_IMAGE_TYPE} from '../../constants'
import {ArrowLeftRound2Icon, ArrowRightRound2Icon} from '../../components/icons'
import ProductAvailability from '../../pages/product-detail/partials/product-availability'
import ProductDisplayPrice from '../../components/product-display-price'
import useBreakpoints from '../../hooks/use-breakpoints'
import {useCroVariantActive} from '../../components/cro'
import AddToBabylist from '../../components/add-to-babylist'
import {useBabylistEnabled} from '../../babylist-api'
import DynamicImage from '../../components/dynamic-image'
import {useCommerceData} from '../../hooks/use-commerce-data'

const IMAGE_WIDTHS = {base: '50vw', lg: '500px'}

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */
const ProductView = ({
    messages,
    product,
    updateCart,
    isProductLoading,
    isMasterProductLoading,
    isBonusProduct,
    isProductASet,
    isSelectable,
    isSetProductSelected,
    context,
    promotion,
    onClose = noop,
    notifyMe,
    onChangedMaterial = noop,
    onSetProductSelect,
    showAddToCartModal,
    variant,
    ...props
}) => {
    const intl = useIntl()
    const toast = useToast()
    const {
        isOrderable,
        showLoading,
        showInventoryMessage,
        quantity,
        variant: productVariant,
        variationParams,
        configurator3DLink
    } = useDerivedProduct(product, isProductASet)
    const babylistEnabled = useBabylistEnabled()
    const styles = useMultiStyleConfig('ProductView', {variant})
    const imageStyles = useMultiStyleConfig('ImageGallery', {variant: 'cockpit'})
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const {isEcommerceEnabled} = useCommerceData()

    const isCro63Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro63PdpHideRating.experimentId,
        CRO_EXPERIMENTS.cro63PdpHideRating.variantId
    )

    const canOrder = useMemo(
        () => !isProductLoading && !isMasterProductLoading && isOrderable,
        [isMasterProductLoading, isProductLoading, isOrderable]
    )

    // This complex logic is needed to close the quick view modal after the product is added to the cart and add to cart modal is shown
    const [addedToCart, setAddedToCart] = useState(false)
    const onAfterAddToCart = useCallback(() => {
        setAddedToCart(true)
    }, [])
    const onAddedToCart = useEffectEvent(() => {
        onClose()
    })
    useEffect(() => {
        if (addedToCart) {
            setAddedToCart(false)
            onAddedToCart()
        }
    }, [addedToCart, onAddedToCart])

    const renderActionButtons = () => {
        const buttons = []

        const handleUpdateCart = async () => {
            onClose()

            if (!canOrder) {
                toast({
                    title: intl.formatMessage({
                        defaultMessage: 'Please select all your options above',
                        id: 'product_view.msg.select_options'
                    }),
                    status: 'error'
                })
                return null
            }
            if (!updateCart) {
                return null
            }

            if (updateCart) {
                await updateCart(productVariant || product, quantity, promotion)
            }
        }

        if (updateCart && !showInventoryMessage) {
            buttons.push(
                <Button
                    key="update-cart-button"
                    onClick={handleUpdateCart}
                    width="100%"
                    variant="solid"
                    marginBottom={2}
                >
                    <FormattedMessage defaultMessage="Update" id="product_view.button.update" />
                </Button>
            )
        } else if (isProductASet) {
            if (isSelectable) {
                if (!isSetProductSelected && isOrderable) {
                    buttons.push(
                        <Button
                            key="choose-this-combination-button"
                            onClick={() => {
                                onSetProductSelect?.()
                                onClose?.()
                            }}
                            width="100%"
                            variant="solidGreen"
                            marginBottom={2}
                        >
                            <FormattedMessage defaultMessage="Add" id="product_view.button.add" />
                        </Button>
                    )
                }
            } else {
                buttons.push(
                    <Button
                        key="choose-this-combination-button"
                        onClick={onClose}
                        width="100%"
                        variant="outline"
                        marginBottom={2}
                    >
                        <FormattedMessage
                            defaultMessage="Choose this combination"
                            id="product_view.button.choose_this_combination"
                        />
                    </Button>
                )
            }
        } else {
            buttons.push(
                <AddToCartCTA
                    key="add-to-cart-notify-me-button"
                    product={product}
                    showAddToCartModal={showAddToCartModal || !isBonusProduct}
                    label={
                        isBonusProduct &&
                        intl.formatMessage({
                            defaultMessage: 'Add bonus product',
                            id: 'product_view.button.add_bonus_product'
                        })
                    }
                    variant={!isBonusProduct ? 'solidGreen' : 'solid'}
                    marginBottom={2}
                    onAfterAddToCart={() => {
                        if (context === TILE_CONTEXT_ACCESSORIES) {
                            trackProductViewAddToCart(context, product.id)
                        }
                        onAfterAddToCart()
                    }}
                    {...props}
                />
            )
        }

        if (product && !isProductASet) {
            buttons.push(
                <Button
                    onClick={() => {
                        if (context === TILE_CONTEXT_ACCESSORIES) {
                            trackProductViewViewProduct(context, product.id)
                        }
                    }}
                    key="view_product"
                    as={Link}
                    to={productUrlBuilder(product, intl.locale)}
                    variant="outline"
                    marginBottom={4}
                    width={'100%'}
                >
                    <FormattedMessage defaultMessage="View product" id="global.viewproduct" />
                </Button>
            )
        }

        return buttons
    }

    const image = useMemo(
        () =>
            findImagesBy(
                product?.imageGroups,
                product?.c_extraMainImage,
                product?.c_extraMainImage2,
                {
                    viewType: MAIN_IMAGES_IMAGE_TYPE,
                    selectedVariationAttributes: variationParams
                }
            )?.[0],
        [
            product?.imageGroups,
            variationParams,
            product?.c_extraMainImage,
            product?.c_extraMainImage2
        ]
    )

    const galleryImagesLimit = useBreakpoints({base: 1, lg: 4})

    const mainGalleryImages = useMemo(
        () =>
            isProductASet
                ? findImagesBy(
                      product?.imageGroups,
                      product?.c_extraMainImage,
                      product?.c_extraMainImage2,
                      {
                          viewType: MAIN_IMAGES_IMAGE_TYPE,
                          selectedVariationAttributes: variationParams
                      }
                  ).slice(0, galleryImagesLimit)
                : [],
        [
            galleryImagesLimit,
            isProductASet,
            product?.imageGroups,
            variationParams,
            product?.c_extraMainImage,
            product?.c_extraMainImage2
        ]
    )

    return (
        <Grid {...styles.container}>
            {/* Top row - Left column */}
            <GridItem {...styles.imageContainer}>
                {product && !isMasterProductLoading ? (
                    isProductASet ? (
                        <ImageGallery
                            items={mainGalleryImages}
                            showPagination
                            variant="setProduct"
                            navLeftIcon={<ArrowLeftRound2Icon boxSize={8} />}
                            navRightIcon={<ArrowRightRound2Icon boxSize={8} />}
                            imageProps={{
                                imageProps: {
                                    alignSelf: 'center',
                                    objectFit: 'contain'
                                },
                                widths: IMAGE_WIDTHS
                            }}
                        />
                    ) : (
                        <>
                            <Box {...imageStyles.imageBackgroundOverlay}></Box>
                            <DynamicImage
                                src={`${image?.disBaseLink || image?.link}[?sw={width}&q=60]`}
                                imageProps={{
                                    alt: image?.title,
                                    alignSelf: 'center',
                                    objectFit: 'contain'
                                }}
                                widths={IMAGE_WIDTHS}
                            />
                        </>
                    )
                ) : (
                    <ImageGallerySkeleton />
                )}
            </GridItem>
            <GridItem position="relative">
                {showLoading ? (
                    <HStack spacing={5} mb={4}>
                        <Skeleton height={6} width={32} />
                        <Skeleton height={6} width={32} />
                    </HStack>
                ) : (
                    <Flex direction="row" flexWrap="wrap" gap={5} mb={4}>
                        {hasProductRating && !isCro63Active && (
                            <BVProductRating product={product} variant="productTile" />
                        )}
                        <BadgeGroup product={product} />
                    </Flex>
                )}

                <Flex direction="column" gap={2} alignItems="start">
                    <Skeleton isLoaded={product?.name}>
                        <Text as="h2" textStyle="Regular 2XLarge">
                            {product?.['c_masterName'] || product?.name}
                        </Text>
                    </Skeleton>

                    {isProductASet && (
                        <>
                            {product?.shortDescription && (
                                <Text textStyle="Regular Small" noOfLines={{base: 6, lg: 3}}>
                                    {product.shortDescription}
                                </Text>
                            )}
                            <Button
                                as={Link}
                                to={productUrlBuilder(product, intl.locale)}
                                variant="link"
                                size="sm"
                                bottom={0}
                                isExternal
                            >
                                <FormattedMessage
                                    defaultMessage="See more details"
                                    id="product_view.button.see_more_details"
                                />
                            </Button>
                        </>
                    )}
                </Flex>

                {!isProductASet &&
                    (showLoading ? (
                        <>
                            {/* First Attribute Skeleton */}
                            <Skeleton height={6} width={32} />
                            <Skeleton height={20} width={64} />

                            {/* Second Attribute Skeleton */}
                            <Skeleton height={6} width={32} />
                            <Skeleton height={20} width={64} />
                        </>
                    ) : (
                        <Stack
                            spacing={8}
                            mt={{base: 4, lg: 8}}
                            __css={{
                                // extend variation container list to edge of screen as per design
                                '.swatch-group-list': {
                                    marginRight: {base: -3.5, md: 0}
                                }
                            }}
                        >
                            {isProductASet && (
                                <MaterialVariationSelection
                                    product={product}
                                    onChange={onChangedMaterial}
                                />
                            )}

                            <ColorVariationSelection
                                product={product}
                                onChange={() => {
                                    if (context === TILE_CONTEXT_ACCESSORIES) {
                                        trackProductViewColorSelect(context, product.id)
                                    }
                                }}
                                isProductASet={isProductASet}
                            />
                        </Stack>
                    ))}
            </GridItem>

            {/* Bottom row */}
            <GridItem colSpan={{base: 2, lg: 1}}>
                {isProductASet &&
                    (showLoading ? (
                        <>
                            {/* First Attribute Skeleton */}
                            <Skeleton height={6} width={32} />
                            <Skeleton height={20} width={64} />

                            {/* Second Attribute Skeleton */}
                            <Skeleton height={6} width={32} />
                            <Skeleton height={20} width={64} />
                        </>
                    ) : (
                        <Stack
                            spacing={8}
                            {...styles.variationContainer}
                            __css={{
                                // extend variation container list to edge of screen as per design
                                '.swatch-group-list': {
                                    marginRight: {base: -3.5, md: 0}
                                }
                            }}
                        >
                            {isProductASet && (
                                <MaterialVariationSelection
                                    product={product}
                                    onChange={onChangedMaterial}
                                />
                            )}

                            <ColorVariationSelection
                                product={product}
                                loading={isMasterProductLoading}
                                onChange={() => {
                                    if (context === TILE_CONTEXT_ACCESSORIES) {
                                        trackProductViewColorSelect(context, product.id)
                                    }
                                }}
                                isProductASet={isProductASet}
                            />
                        </Stack>
                    ))}
                {!isBonusProduct && !isProductASet && (
                    <ProductVRLinks
                        configurator3DLink={configurator3DLink}
                        product={product}
                        onConfiguratorClick={() => {
                            if (context === TILE_CONTEXT_ACCESSORIES) {
                                trackProductViewConfigurator(context, product.id)
                            }
                        }}
                    />
                )}
                <HStack
                    align="start"
                    justifyContent="space-between"
                    {...styles.availabilityPriceContainer}
                >
                    {isEcommerceEnabled && (
                        <>
                            <ProductAvailability product={product} />
                            <ProductDisplayPrice
                                product={product}
                                isBonusProduct={isBonusProduct}
                                loading={showLoading}
                                variant="pdp"
                            />
                        </>
                    )}
                </HStack>

                {messages?.length > 0 && (
                    <Flex flexDirection="column" gap={4} marginBottom={4}>
                        {messages.map((message, index) => (
                            <Text
                                key={index}
                                textStyle="Regular Small"
                                backgroundColor={message.background}
                                color={message.color}
                                padding={4}
                                borderRadius="base"
                            >
                                {message.text}
                            </Text>
                        ))}
                    </Flex>
                )}

                {renderActionButtons()}

                {isBonusProduct && (
                    <Button
                        variant="link"
                        onClick={() => {
                            onClose()
                        }}
                    >
                        <FormattedMessage
                            id="product_view.button.do_not_want_bonus_product"
                            defaultMessage="I don't want a bonus product"
                        />
                    </Button>
                )}

                {babylistEnabled && (
                    <Stack paddingTop={2}>
                        <AddToBabylist product={product} />
                    </Stack>
                )}
            </GridItem>
        </Grid>
    )
}

ProductView.propTypes = {
    messages: PropTypes.array,
    promotion: PropTypes.object,
    product: PropTypes.object,
    isMasterProductLoading: PropTypes.bool,
    isProductLoading: PropTypes.bool,
    updateCart: PropTypes.func,
    isBonusProduct: PropTypes.bool,
    isProductASet: PropTypes.bool,
    isSelectable: PropTypes.bool,
    isSetProductSelected: PropTypes.bool,
    context: PropTypes.string,
    onClose: PropTypes.func,
    notifyMe: PropTypes.func,
    onChangedMaterial: PropTypes.func,
    showAddToCartModal: PropTypes.bool,
    variant: PropTypes.string,
    onSetProductSelect: PropTypes.func
}

export default ProductView
